import { c as _c } from "react/compiler-runtime";
import { ProfileUI } from 'components/portal/profileTypes';
import { DateTime } from 'luxon';
import { pipe, take } from 'remeda';
import { compactObj, isNullOrEmpty } from 'shared/collection';
import { err, ok, okOrErrPartition, okOrThrow, sansOk } from 'shared/promise';
import { sb } from 'utils/db';
import { ExportTable, createMappingFromIds, getExportMd, getMappedRow, newId } from './export';
import { match } from 'ts-pattern';
import { asyncMap } from 'modern-async';
import { usePortal } from 'components/portal/portal';
import { useMutation } from '@tanstack/react-query';
import { parseObjOrErr, parseOrErr, safeParseList } from 'shared/type';
type DupeType = 'question' | 'subquestion';
export const dupeQuestion = async ({
  type,
  copy_forecasts,
  profile,
  id
}: {
  type: DupeType;
  copy_forecasts: boolean;
  profile: ProfileUI;
  id: string;
}) => {
  const mainRow = await sb.from(type).select('*').match({
    id
  }).then(rs => {
    if (rs.error || isNullOrEmpty(rs.data)) return err(`${type} ${id} not found`);
    return parseObjOrErr(getExportMd(type).schema, rs.data[0!]);
  });
  if (!mainRow.ok) return mainRow;
  const mainIdNew = newId();
  const mainRowNew = await sb.from(type).insert({
    ...sansOk(mainRow),
    id: mainIdNew,
    cfg: {
      ...mainRow.cfg,
      title: `${mainRow.cfg.title} (copy)`
    },
    created_by: profile.id,
    created_at: DateTime.utc().toISO(),
    updated_at: DateTime.utc().toISO()
  } as any);
  if (mainRowNew.error) return err(mainRowNew.error.message);
  const tablesToCopy = match(type as DupeType).with('question', () => ['subquestion', 'forecast', 'comment', 'reaction'] as const).with('subquestion', () => ['forecast', 'comment'] as const).exhaustive().filter(t => copy_forecasts ? true : !['forecast', 'comment'].includes(t));
  let rowIdMappings: Partial<Record<ExportTable, Map<string, string>>> = {
    [type]: new Map([[mainRow.id, mainIdNew]])
  };
  await asyncMap(tablesToCopy, async t => {
    const rows = await sb.from(t).select('*').match({
      ...match(type as DupeType).with('question', () => ({
        question_id: mainRow.id
      })).with('subquestion', () => ({
        subquestion_id: mainRow.id
      })).exhaustive(),
      ...match(t).with('forecast', () => ({})).otherwise(() => ({
        deleted: false
      }))
    }).throwOnError().then(rs => safeParseList(getExportMd(t).schema.strip(), rs.data ?? []));
    rowIdMappings[t] = createMappingFromIds(rows as {
      id: string;
    }[]);
    const [mappedRows, mappedRowsErr] = pipe(rows.map(row => ({
      ...getMappedRow({
        row: row as any,
        table: t,
        rowIdMappings,
        ensureIdMapped: true
      })
    })), rs => okOrErrPartition(rs));
    // if (mappedRowsErr.length > 0)
    //   throw new Error(
    //     `Failed to map rows for import in ${t}: ${take(mappedRowsErr, 3)
    //       .map(e => e.message)
    //       .join('\n')}`
    //   )
    const res = await sb.from(t).insert(mappedRows as any);
    if (res.error) throw new Error(`unable to duplicate ${t}: ${res.error.message}`);
  });
  return ok(mainIdNew);
};
export const useDupeQuestion = type => {
  const $ = _c(3);
  const {
    profile
  } = usePortal();
  let t0;
  if ($[0] !== profile || $[1] !== type) {
    t0 = {
      mutationFn: async t1 => {
        const {
          copy_forecasts,
          id
        } = t1;
        return dupeQuestion({
          type,
          copy_forecasts,
          profile,
          id
        }).then(_temp);
      }
    };
    $[0] = profile;
    $[1] = type;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  return useMutation(t0);
};
function _temp(r) {
  return okOrThrow(r);
}